import { useRef, useState } from 'react';
import { ArrowLeft, Printer, Send, FileDown } from 'lucide-react';
import { Proposta, propostaService } from '../../../services/propostaService';
import { PropostaVisualizacao } from '../../../components/PropostaTemplates';

interface PropostaPreviewProps {
  proposta: Proposta;
  onBack: () => void;
}

export function PropostaPreview({ proposta, onBack }: PropostaPreviewProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const formatNumero = () => {
    const num = String(proposta.numero).padStart(3, '0');
    const data = new Date(proposta.dataEmissao);
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${num}-${mes}-${ano}`;
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    const clienteNome = proposta.client.razaoSocial.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    document.title = `Proposta #${formatNumero()} - ${clienteNome}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleEnviar = async () => {
    if (confirm('Deseja marcar esta proposta como enviada?')) {
      try {
        await propostaService.updateStatus(proposta.id, 'enviada');
        alert('Proposta marcada como enviada!');
      } catch (error) {
        console.error('Erro:', error);
      }
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setGeneratingPdf(true);
      await propostaService.downloadPdf(proposta.id, proposta.numero);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF da proposta');
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between no-print">
        <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <div className="flex gap-2">
          {proposta.status === 'rascunho' && (
            <button onClick={handleEnviar} className="flex items-center gap-2 px-4 py-2 btn btn-primary">
              <Send className="w-4 h-4" /> Marcar como Enviada
            </button>
          )}
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={generatingPdf}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            <FileDown className="w-4 h-4" />
            {generatingPdf ? 'Gerando...' : 'Gerar PDF'}
          </button>
        </div>
      </div>

      {/* Documento */}
      <div ref={printRef} id="proposta-print" className="bg-white p-8 rounded-lg border">
        <PropostaVisualizacao proposta={proposta} />
      </div>

      {/* Estilos de impressão */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #proposta-print, #proposta-print * { visibility: visible; }
          #proposta-print { position: absolute; left: 0; top: 0; width: 100%; background: white !important; border: none !important; }
          .no-print { display: none !important; }
          @page { margin: 1cm; size: A4; }
        }
      `}</style>
    </div>
  );
}
